import { env } from "@/config/env";
import { CartItem, LineItem } from "@/types";
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "../Auth";

interface LineItemsUpdatedEvent {
    lineItems: LineItem[];
}

interface CartUpdatedEvent {
    items: CartItem[];
    total: number;
}

interface JobStatusUpdatedEvent {
    jobId: string;
    status: "Pending" | "InProgress" | "Completed" | "Failed";
    message?: string;
}

interface JobCompletedEvent {
    jobId: string;
    message?: string;
    resultUrl?: string;
}

interface Job {
    id: string;
    status: "Pending" | "InProgress" | "Completed" | "Failed";
    message?: string;
    resultUrl?: string;
}

interface HubContextType {
    // Line items (for Invoice provider)
    hubLineItems: LineItem[] | null;
    clearHubLineItems: () => void;

    // Cart state (for Cart provider)
    hubCartItems: CartItem[] | null;
    hubCartTotal: number | null;
    clearHubCart: () => void;
    // Jobs state
    hubJobs: Record<string, Job>;
    clearHubJobs: () => void;
    removeHubJob: (jobId: string) => void;

    // Read-only admin dashboard refresh ping (timestamp of last snapshot update)
    dashboardUpdatedAt: number;

    isConnected: boolean;
}

const HubContext = createContext<HubContextType | null>(null);

export const HubProvider = ({ children }: { children: React.ReactNode }) => {
    const [hubLineItems, setHubLineItems] = useState<LineItem[] | null>(null);
    const [hubCartItems, setHubCartItems] = useState<CartItem[] | null>(null);
    const [hubCartTotal, setHubCartTotal] = useState<number | null>(null);
    const [hubJobs, setHubJobs] = useState<Record<string, Job>>({});
    const [dashboardUpdatedAt, setDashboardUpdatedAt] = useState<number>(0);
    const [isConnected, setIsConnected] = useState(false);
    const { currentUser } = useAuth();
    const connectionRef = useRef<HubConnection | null>(null);

    const clearHubLineItems = useCallback(() => {
        setHubLineItems(null);
    }, []);

    const clearHubCart = useCallback(() => {
        setHubCartItems(null);
        setHubCartTotal(null);
    }, []);

    const clearHubJobs = useCallback(() => {
        setHubJobs({});
    }, []);

    const removeHubJob = useCallback((jobId: string) => {
        setHubJobs((prevJobs) => {
            const updatedJobs = { ...prevJobs };
            delete updatedJobs[jobId];
            return updatedJobs;
        });
    }, []);

    useEffect(() => {
        // Only connect when user is authenticated
        if (!currentUser) {
            // Disconnect if user logs out
            if (connectionRef.current) {
                connectionRef.current.stop();
                connectionRef.current = null;
                setIsConnected(false);
                setHubLineItems(null);
                setHubCartItems(null);
                setHubCartTotal(null);
            }
            return;
        }

        // Don't create a new connection if one already exists
        if (connectionRef.current) {
            return;
        }

        const hubUrl = `${env.API_URL}/hub`;

        const connection = new HubConnectionBuilder()
            .withUrl(hubUrl, {
                withCredentials: true,
                // Fallback: also send JWT via query string for WebSocket connections
                // where cookies may not be available (cross-origin, strict privacy settings)
                accessTokenFactory: () => {
                    const match = document.cookie.match(/(?:^|;\s*)jwt=([^;]*)/);
                    return match ? match[1] : '';
                }
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
            .configureLogging(LogLevel.Information)
            .build();

        connectionRef.current = connection;

        // Handle connection state changes
        connection.onreconnecting(() => {
            // console.log("SignalR: Reconnecting...");
            setIsConnected(false);
        });

        connection.onreconnected(() => {
            // console.log("SignalR: Reconnected");
            setIsConnected(true);
        });

        connection.onclose(() => {
            // console.log("SignalR: Connection closed");
            setIsConnected(false);
        });

        // Listen for LineItemsUpdated events
        connection.on("LineItemsUpdated", (data: LineItemsUpdatedEvent) => {
            // console.log("SignalR: LineItemsUpdated received", data);
            setHubLineItems(data.lineItems);
        });

        // Listen for CartUpdated events
        connection.on("CartUpdated", (data: CartUpdatedEvent) => {
            // console.log("SignalR: CartUpdated received", data);
            setHubCartItems(data.items);
            setHubCartTotal(data.total);
        });

        connection.on("JobStatusUpdated", (data: JobStatusUpdatedEvent) => {
            // console.log("SignalR: JobStatusUpdated received", data);
            setHubJobs((prevJobs) => ({
                ...prevJobs,
                [data.jobId]: {
                    id: data.jobId,
                    status: data.status,
                    message: data.message
                }
            }));
        });
        connection.on("JobCompleted", (data: JobCompletedEvent) => {
            // console.log("SignalR: JobCompleted received", data);
            setHubJobs((prevJobs) => ({
                ...prevJobs,
                [data.jobId]: {
                    id: data.jobId,
                    status: "Completed",
                    message: data.message,
                    resultUrl: data.resultUrl
                }
            }));
        });

        // Payload-free ping: the read-only admin dashboard snapshot was refreshed
        connection.on("ReadOnlyDashboardUpdated", () => {
            // console.log("SignalR: ReadOnlyDashboardUpdated received");
            setDashboardUpdatedAt(Date.now());
        });

        // Start the connection
        const startConnection = async () => {
            try {
                await connection.start();
                // console.log("SignalR: Connected");
                setIsConnected(true);
            } catch (err) {
                console.error("SignalR: Connection failed", err);
                setIsConnected(false);
            }
        };

        startConnection();

        // Cleanup on unmount
        return () => {
            if (connectionRef.current && connectionRef.current.state !== HubConnectionState.Disconnected) {
                connectionRef.current.stop();
                connectionRef.current = null;
            }
        };
    }, [currentUser]);

    const value: HubContextType = {
        hubLineItems,
        clearHubLineItems,
        hubCartItems,
        hubCartTotal,
        clearHubCart,
        hubJobs,
        clearHubJobs,
        removeHubJob,
        dashboardUpdatedAt,
        isConnected
    };

    return (
        <HubContext.Provider value={value}>{children}</HubContext.Provider>
    );
};

export const useHub = () => {
    const context = useContext(HubContext);
    if (!context) {
        throw new Error("useHub must be used within a HubProvider");
    }
    return context;
};
